<script setup>
// core
import {computed, onMounted, ref, getCurrentInstance, useTemplateRef} from "vue";
import {useStore} from "vuex";
import {useInfiniteScroll} from "@vueuse/core";
import useScrollFade from "@/components/Telephony/api/useScrollFade";

// components
import CallNumberView from "@/components/Telephony/CallNumberView.vue";
import SipServerInfo from "@/components/Telephony/SipServerInfo.vue";
import SipExtensionCard from "@/components/Telephony/SipExtensionCard.vue";
import BaseSearchInput from "@/components/ui/BaseSearchInput.vue";
import ButtonLoading from "@/components/Recurring/ButtonLoading.vue";
import InfoDataTextRecurring from "@/components/Recurring/InfoDataTextRecurring.vue";

const scrollComponent = useTemplateRef('scrollComponent')
const isSipTableLoading = ref(false)
const isLoadingMore = ref(false)
const isSipSearchLoading = ref(false)
const loadingExtension = ref('')
const store = useStore()
const {proxy} = getCurrentInstance()
const checkedExtensions = ref(new Map())

const searchQ = ref('')

// computed
const info = computed(() => store.state.dialogParams?.info)
const extensionsList = computed(() => store.getters['telephony/sips'])
const {isBottom} = useScrollFade(scrollComponent, () => extensionsList.value?.length)
const extensionsMeta = computed(() => store.getters['telephony/sipsMeta'])
const checkedCount = computed(() => checkedExtensions.value.size)
const totalCount = computed(() => extensionsMeta.value?.total ?? extensionsList.value?.length ?? 0)

// methods

const isChecked = (item) =>
    checkedExtensions.value.has(item.credentials.username)

const toggleExtension = async (ex) => {
    const username = ex?.credentials?.username
    const number = info.value?.number
    if (!username || !number) return

    const map = checkedExtensions.value
    const wasChecked = map.has(username)
    const type = wasChecked ? 'unbind' : 'bind'

    const params = {number, username, type, moshaToast: proxy.$moshaToast}

    loadingExtension.value = username

    try {
        await store.dispatch('telephony/toggleExtensionByNumber', params)
        if (wasChecked) map.delete(username)
        else map.set(username, true)
    } catch (e) {
        if (wasChecked) map.set(username, true)
        else map.delete(username)
    } finally {
        loadingExtension.value = null
    }
}

const loadExtensions = async (page = 1, search = undefined, append = false, perPage = 5) => {
    // appending keeps the list on screen and only shows a spinner row at its end
    const flag = append ? isLoadingMore : isSipTableLoading
    flag.value = true

    try {
        await store.dispatch('telephony/getSips', {
            query: {
                page: search ? 1 : page,
                per_page: perPage,
                name: search || undefined
            },
            moshaToast: proxy.$moshaToast,
            append
        })
    } finally {
        flag.value = false
    }
}

const loadMoreExtensions = async () => {
    const currentPage = extensionsMeta.value.current_page || 1
    const totalPages = extensionsMeta.value.last_page || 1

    if (currentPage < totalPages && !isSipTableLoading.value && !isLoadingMore.value) {
        await loadExtensions(currentPage + 1, searchQ.value, true)
    }
}

const onSipSearchApply = async () => {
    isSipSearchLoading.value = true
    await loadExtensions(1, searchQ.value)
    isSipSearchLoading.value = false
}

// edit / delete
const openExtensionDialog = (name, ex) => {
    store.commit('setNextDialog', 'SelectSipDialog')
    store.commit('openDialog', {
        name,
        number: info.value?.number,
        info: info.value,
        currentExtension: ex,
        next: 'SelectSipDialog',
    })
}

const editExtension = (ex) => openExtensionDialog('EditExtensionDialog', ex)

const deleteExtension = (ex) => openExtensionDialog('DeleteSipDialog', ex)

// create
const createExtension = () => {
    store.commit('setNextDialog', 'SelectSipDialog');
    store.commit("setDialog", "CreateSip");
}

onMounted(async () => {
    // the bound usernames come from dialogParams, so the checkboxes are correct
    // on the very first render — no need to wait for the list request
    checkedExtensions.value = new Map(
        (info.value?.extensions ?? []).map(ext => [ext, true])
    )

    await loadExtensions()
})

useInfiniteScroll(
    scrollComponent,
    async () => {
        await loadMoreExtensions()
    },
    {
        distance: 50,
        interval: 300,
    }
);
</script>

<template>
    <slot name="header_close"/>

    <div class="wrapper">
        <div class="wrapper__head">
            <p class="wrapper__title">{{ $t('telephony.sip.title') }}</p>
            <CallNumberView :number="info?.number"/>
        </div>

        <div class="wrapper__intro">
            <p v-for="(text, i) in $tm('telephony.sip.intro')" :key="i">{{ text }}</p>
        </div>

        <SipServerInfo/>

        <div class="wrapper__listhead">
            <span class="wrapper__count">
                <i class="bi bi-headset wrapper__count-icon"></i>
                <span class="wrapper__count-value">
                    {{ $t('telephony.sip.connected_count', {current: checkedCount, total: totalCount}) }}
                </span>
            </span>

            <BaseSearchInput
                v-model="searchQ"
                :loading="isSipSearchLoading"
                @submit-search="onSipSearchApply"
            />
        </div>

        <ul ref="scrollComponent" class="wrapper__list">
            <SipExtensionCard
                v-for="item in extensionsList"
                :key="item.credentials.username"
                :extension="item"
                :checked="isChecked(item)"
                :toggle-loading="loadingExtension === item.credentials.username"
                @toggle="toggleExtension"
                @edit="editExtension"
                @delete="deleteExtension"
            />

            <li v-if="isLoadingMore" class="wrapper__more">
                <span class="spinner"></span>
            </li>

            <InfoDataTextRecurring
                v-if="isSipTableLoading && !extensionsList?.length"
                value="loading"
                height="240px"
            />
            <InfoDataTextRecurring
                v-if="!extensionsList?.length && !isSipTableLoading"
                value="noSip"
                height="120px"
            />

            <li class="wrapper__fade" :class="{ 'is-bottom': isBottom || isLoadingMore }" aria-hidden="true"></li>
        </ul>

        <div class="wrapper__footer">
            <ButtonLoading class="ec_button" @click="createExtension">
                {{ $t('telephony.sip.new') }}
            </ButtonLoading>

            <p class="wrapper__footnote">
                {{ $t('telephony.sip.new_note', {number: info?.number}) }}
            </p>
        </div>
    </div>
</template>

<style scoped lang="scss">
@use "@/styles/scroll-fade.scss";
.wrapper {
    font-family: 'Gilroy', sans-serif;
    padding: 22px 28px 24px;
    border-radius: 7px;
    background-color: #ffffff;

    display: flex;
    flex-direction: column;
    gap: 16px;

    max-height: calc(100vh - 80px);
    // auto, not hidden: when even the minimums of the open server block and of
    // the list do not fit, the dialog scrolls instead of clipping its footer
    overflow: hidden auto;
    box-sizing: border-box;

    // .number-settings__head
    &__head {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
    }

    &__intro {
        display: flex;
        flex-direction: column;
        gap: 10px;

        p {
            margin: 0;
            font-family: 'Gilroy', sans-serif;
            font-size: 15px;
            font-weight: 500;
            line-height: 1.6;
            color: var(--text-third-color);
        }
    }

    // .title from SettingsNumberDialog
    &__title {
        margin: 0;
        padding: 0;
        font-size: 20px;
        text-align: center;
        font-family: Gilroy, sans-serif;
        font-weight: 500;
        text-transform: uppercase;
        color: var(--text-color);
    }

    &__listhead {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        flex-wrap: wrap;
    }

    &__count {
        display: inline-flex;
        align-items: center;
        gap: 10px;

        font-size: 20px;
        font-family: Gilroy, sans-serif;
        font-weight: 500;
        text-transform: uppercase;
        color: var(--text-color);

        &-icon {
            font-size: 22px;
            line-height: 1;
            color: var(--text-color);
        }

        // the colon comes from CSS, as it does for the field labels
        &-value {
            letter-spacing: 0.05rem;

            &::after {
                content: ":";
            }
        }
    }

    &__list {
        list-style: none;
        margin: 0;
        padding: 0;

        display: flex;
        flex-direction: column;
        gap: 10px;

        // only the list stretches, so expanding the server block steals space
        // from it instead of making the dialog taller, but one card always
        // stays in sight
        flex: 1 1 auto;
        min-height: 104px;
        overflow-y: auto;

        padding-right: 10px;
        margin-right: -14px;

        &::-webkit-scrollbar {
            width: 3px;
        }

        &::-webkit-scrollbar-track {
            background: #ffffff;
        }

        &::-webkit-scrollbar-thumb {
            background: #7970dfbf;
            border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: var(--general-color);
        }
    }

    &__fade {
        @include scroll-fade.scroll-fade(10px);
    }

    // a spinner row while the next page is being appended
    &__more {
        flex: 0 0 auto;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &__footer {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
        padding-top: 4px;
    }

    &__footnote {
        margin: 0;
        font-family: 'Gilroy', sans-serif;
        font-size: 12px;
        font-weight: 500;
        letter-spacing: 0.05rem;
        text-transform: uppercase;
        color: var(--border-primary-color);
    }
}

// breakpoints match SettingsNumberDialog
@media (max-width: 570px) {
    .wrapper {
        // the heading and the search stack instead of sharing a row
        &__listhead {
            align-items: flex-start;
        }

        &__listhead:deep(.search-ui) {
            margin-left: 0;
            width: 100%;
        }

        &__listhead:deep(.search-ui__input) {
            width: 100%;
        }
    }
}

@media (max-width: 420px) {
    .wrapper {
        padding: 16px 14px 18px;
    }
}
</style>
